// Descuento global (resumen.descuNoSuj / descuExenta / descuGravada del DTE): disminucion aplicada al total de cada
// tipo de venta, no por producto. Misma regla que api-facturacion (BLL/Utilities/DescuentoGlobalHelper.cs); si una
// cambia, la otra debe cambiar igual. Referencia: Manual Funcional del Sistema de Transmision v2.0, pags. 46-49.

export type TipoDescuentoGlobal = 'monto' | 'porcentaje'

export interface DescuentoGlobalForm {
  tipo: TipoDescuentoGlobal
  valor: string
}

export interface DescuentoGlobalCalculo {
  descuGravada: number
  descuExenta: number
  descuNoSuj: number
  total: number
  /** porcentajeDescuento del DTE: el porcentaje capturado, o 0 cuando se capturo un monto */
  porcentajeDescuento: number
  /** Mensaje para mostrar al usuario; vacio cuando el descuento es valido o no hay descuento */
  error: string
}

export const DESCUENTO_GLOBAL_INICIAL: DescuentoGlobalForm = {tipo: 'monto', valor: ''}

/** Con 100% el total a pagar seria $0.00, valor que FELES no emite. */
export const PORCENTAJE_MAXIMO = 99.99
/** Venta neta minima que debe quedar despues del descuento. */
export const NETO_MINIMO = 0.01

const SIN_DESCUENTO: DescuentoGlobalCalculo = {
  descuGravada: 0,
  descuExenta: 0,
  descuNoSuj: 0,
  total: 0,
  porcentajeDescuento: 0,
  error: '',
}

/** Redondeo a 2 decimales, mitad hacia arriba (igual que MidpointRounding.AwayFromZero en la API). */
export const redondear2 = (n: number): number => Math.round(parseFloat((n * 100).toFixed(8))) / 100

/** Solo digitos y un punto, con maximo 2 decimales (admite 14.05). */
export const esEntradaDescuentoValida = (valor: string): boolean => /^\d*(\.\d{0,2})?$/.test(valor)

const conError = (error: string): DescuentoGlobalCalculo => ({...SIN_DESCUENTO, error})

/**
 * @param gravada total de ventas gravadas, ya neto de los descuentos por item
 * @param exenta total de ventas exentas, ya neto de los descuentos por item
 * @param noSuj total de ventas no sujetas, ya neto de los descuentos por item
 */
export const calcularDescuentoGlobal = (
  form: DescuentoGlobalForm | undefined,
  gravada: number,
  exenta: number,
  noSuj: number
): DescuentoGlobalCalculo => {
  const valorTexto = (form?.valor ?? '').trim()
  if (valorTexto === '') return SIN_DESCUENTO

  const valor = parseFloat(valorTexto)
  if (isNaN(valor)) return conError('El valor del descuento global no es un número válido.')
  if (valor < 0) return conError('El descuento global no puede ser negativo.')
  if (valor === 0) return SIN_DESCUENTO
  if (!esEntradaDescuentoValida(valorTexto)) return conError('El descuento global admite como máximo 2 decimales.')

  const bases = [redondear2(gravada), redondear2(exenta), redondear2(noSuj)]
  const totalVentas = bases[0] + bases[1] + bases[2]
  if (totalVentas <= 0) return conError('No hay ventas sobre las cuales aplicar el descuento global.')

  const descuentos = [0, 0, 0]
  let porcentajeDescuento = 0

  if (form?.tipo === 'porcentaje') {
    if (valor > PORCENTAJE_MAXIMO) {
      return conError(`El porcentaje de descuento global no puede ser mayor a ${PORCENTAJE_MAXIMO}%.`)
    }
    // Se aplica a cada tipo de venta y se redondea por tipo (ejemplo del Manual Funcional v2.0, pag. 46)
    for (let i = 0; i < 3; i++) descuentos[i] = redondear2((bases[i] * valor) / 100)
    porcentajeDescuento = valor
  } else {
    if (valor >= totalVentas) return conError('El descuento global no puede ser igual o mayor al total de las ventas.')
    // Reparto proporcional por tipo de venta; el centavo que sobre o falte se ajusta en el tipo mayor
    for (let i = 0; i < 3; i++) descuentos[i] = redondear2((bases[i] * valor) / totalVentas)
    const diferencia = redondear2(valor - (descuentos[0] + descuentos[1] + descuentos[2]))
    if (diferencia !== 0) {
      let mayor = 0
      for (let i = 1; i < 3; i++) if (bases[i] > bases[mayor]) mayor = i
      descuentos[mayor] = redondear2(descuentos[mayor] + diferencia)
    }
  }

  for (let i = 0; i < 3; i++) {
    if (descuentos[i] < 0 || descuentos[i] > bases[i]) {
      return conError('El descuento global no puede ser mayor a las ventas del tipo al que se aplica.')
    }
  }

  const total = redondear2(descuentos[0] + descuentos[1] + descuentos[2])
  if (redondear2(totalVentas - total) < NETO_MINIMO) {
    return conError('El descuento global no puede dejar el documento en $0.00.')
  }

  return {
    descuGravada: descuentos[0],
    descuExenta: descuentos[1],
    descuNoSuj: descuentos[2],
    total,
    porcentajeDescuento,
    error: '',
  }
}

/**
 * Factor (0-1) que deja la base de un tipo de venta neta de su descuento global. Los otros tributos porcentuales
 * (turismo 5%, ad-valorem) se calculan sobre la venta neta, igual que el IVA; los tributos fijos por unidad
 * (FOVIAL, COTRANS) no dependen del precio y no se ajustan.
 */
export const factorNetoDescuento = (base: number, descuento: number): number =>
  base > 0 ? Math.max(base - descuento, 0) / base : 1

/** IVA 13% sobre la venta gravada neta de descuento global (Comprobante de credito fiscal). */
export const ivaSobreBaseNeta = (gravada: number, descuGravada: number): number =>
  redondear2((redondear2(gravada) - descuGravada) * 0.13)

/**
 * IVA incluido en los precios (Factura consumidor final): suma del IVA por item menos el IVA contenido en el
 * descuento global a ventas gravadas.
 */
export const ivaIncluidoTras = (ivaItems: number, descuGravada: number): number =>
  redondear2(ivaItems - redondear2((descuGravada * 13) / 113))

/** Campos que se agregan al documento enviado a la API (vacios cuando no hay descuento global). */
export const payloadDescuentoGlobal = (form: DescuentoGlobalForm | undefined, totalDescuento: number) =>
  totalDescuento > 0
    ? {descuentoGlobalTipo: form?.tipo ?? 'monto', descuentoGlobalValor: (form?.valor ?? '').trim()}
    : {descuentoGlobalTipo: '', descuentoGlobalValor: ''}

/**
 * Notas de credito/debito sobre un documento que tuvo descuento global: el manual indica detallar ese descuento en
 * el campo "descuento por item" (Manual Funcional v2.0, pag. 47). Reparte el descuento global del documento
 * relacionado entre sus items, proporcional a la venta de cada item dentro de su tipo de venta (gravada, exenta o
 * no sujeta); el centavo que sobre o falte se ajusta en el item mayor. Devuelve el monto por item (mismo orden que
 * `cuerpo`); todo en cero si el documento no tuvo descuento global.
 */
export const descuentoGlobalPorItem = (cuerpo: any, resumen: any): number[] => {
  const items: any[] = Array.isArray(cuerpo) ? cuerpo : cuerpo ? [cuerpo] : []
  const partes = new Array<number>(items.length).fill(0)
  const tipos: Array<[string, string]> = [
    ['ventaGravada', 'descuGravada'],
    ['ventaExenta', 'descuExenta'],
    ['ventaNoSuj', 'descuNoSuj'],
  ]

  for (const [campoVenta, campoDescuento] of tipos) {
    const descuento = redondear2(parseFloat(resumen?.[campoDescuento]) || 0)
    if (descuento <= 0) continue

    const ventas = items.map((item) => parseFloat(item?.[campoVenta]) || 0)
    const totalVentas = ventas.reduce((a, b) => a + b, 0)
    if (totalVentas <= 0) continue

    const reparto = ventas.map((venta) => redondear2((venta * descuento) / totalVentas))
    const diferencia = redondear2(descuento - reparto.reduce((a, b) => a + b, 0))
    if (diferencia !== 0) {
      let mayor = 0
      ventas.forEach((venta, i) => {
        if (venta > ventas[mayor]) mayor = i
      })
      reparto[mayor] = redondear2(reparto[mayor] + diferencia)
    }
    reparto.forEach((monto, i) => (partes[i] = redondear2(partes[i] + monto)))
  }
  return partes
}
